import { useLiveQuery } from 'dexie-react-hooks'
import { Panel } from '../comunes/Panel.js'
import { Boton } from '../comunes/Boton.js'
import { db } from '../datos/db.js'
import { encolar } from '../datos/sync.js'
import { fechaLarga, hora } from '../datos/formato.js'

export function DetalleCita({
  citaId,
  onCerrar,
  onAtencion,
  onVerFicha,
}: {
  citaId: string
  onCerrar: () => void
  onAtencion: (citaId: string) => void
  onVerFicha: (clienteId: string) => void
}) {
  const detalle = useLiveQuery(async () => {
    const cita = await db.cita.get(citaId)
    if (!cita) return null
    const cliente = await db.cliente.get(cita.cliente_id)
    const tramos = await db.cita_tramo.where('cita_id').equals(citaId).sortBy('orden')
    const reservas = tramos.length
      ? await db.reserva_recurso.where('cita_tramo_id').anyOf(tramos.map((t) => t.id)).toArray()
      : []
    const recursos = await db.recurso.bulkGet([
      ...new Set(reservas.filter((r) => r.activa).map((r) => r.recurso_id)),
    ])
    return { cita, cliente, tramos, recursos: recursos.filter((r) => r !== undefined) }
  }, [citaId])

  async function cambiarEstado(
    estado: 'confirmada' | 'cancelada' | 'no_asistio' | 'en_atencion' | 'atendida',
  ) {
    await db.cita.update(citaId, { estado })
    await encolar('cambiar_estado', { citaId, estado })
    if (estado === 'cancelada') onCerrar()
  }

  if (detalle === undefined) {
    return <Panel titulo="Detalle de cita" onCerrar={onCerrar}>Cargando…</Panel>
  }
  if (detalle === null) {
    return <Panel titulo="Detalle de cita" onCerrar={onCerrar}>La cita ya no está disponible.</Panel>
  }

  const primero = detalle.tramos[0]
  const ultimo = detalle.tramos.at(-1)
  const terminal = ['cancelada', 'no_asistio', 'facturada'].includes(detalle.cita.estado)

  return (
    <Panel
      titulo={detalle.cliente?.nombre ?? 'Cita sin paciente'}
      bajada={primero ? `${fechaLarga(primero.inicio)} · ${hora(primero.inicio)}` : 'Sin horario'}
      onCerrar={onCerrar}
    >
      <dl className="space-y-4 text-sm">
        <Dato etiqueta="Horario">
          {primero && ultimo ? `${hora(primero.inicio)}–${hora(ultimo.fin)}` : 'Sin horario'}
        </Dato>
        <Dato etiqueta="Estado">{etiquetaEstado(detalle.cita.estado)}</Dato>
        <Dato etiqueta="Recursos">
          {detalle.recursos.length ? detalle.recursos.map((r) => r.nombre).join(' · ') : 'Sin recursos'}
        </Dato>
        <Dato etiqueta="Contacto">{detalle.cliente?.telefono ?? 'Sin teléfono'}</Dato>
        {detalle.cita.notas && <Dato etiqueta="Notas">{detalle.cita.notas}</Dato>}
      </dl>

      <div className="mt-8 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
        <Boton variante="secondary" onClick={() => onVerFicha(detalle.cita.cliente_id)}>
          Ver ficha
        </Boton>
        {!terminal && (
          <>
            {detalle.cita.estado === 'pendiente' && (
              <Boton variante="primary" onClick={() => void cambiarEstado('confirmada')}>
                Confirmar
              </Boton>
            )}
            {detalle.cita.estado === 'confirmada' && (
              <Boton variante="secondary" onClick={() => onAtencion(citaId)}>
                Atender
              </Boton>
            )}
            {detalle.cita.estado === 'en_atencion' && (
              <Boton variante="primary" onClick={() => onAtencion(citaId)}>
                Atender
              </Boton>
            )}
            <Boton variante="secondary" onClick={() => void cambiarEstado('no_asistio')}>
              No asistió
            </Boton>
            <Boton variante="danger" onClick={() => void cambiarEstado('cancelada')}>
              Cancelar cita
            </Boton>
          </>
        )}
      </div>
    </Panel>
  )
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="txt-meta">{etiqueta}</dt>
      <dd className="mt-0.5 text-slate-800">{children}</dd>
    </div>
  )
}

function etiquetaEstado(estado: string): string {
  const etiquetas: Record<string, string> = {
    pendiente: 'Pendiente',
    confirmada: 'Confirmada',
    en_atencion: 'En atención',
    atendida: 'Atendida',
    facturada: 'Facturada',
    cancelada: 'Cancelada',
    no_asistio: 'No asistió',
    pendiente_reprogramar: 'Pendiente de reprogramar',
  }
  return etiquetas[estado] ?? estado
}
